import { useState, useEffect } from 'react';
import {
	Modal,
	Form,
	Input,
	Button,
	Select,
	Card,
	Empty,
	Table,
	TableProps,
	Row,
	Col,
	DatePicker,
	Typography,
	Upload,
	Flex,
	Tag,
	Tooltip,
	notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
	ColumnsType,
	FilterDropdownProps,
	SorterResult,
} from 'antd/es/table/interface';
import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';
import {
	useGetGagesQuery,
	useCreateGageMutation,
	useUpdateGageMutation,
	useGetGageCategoriesQuery,
	useGetGageTypesQuery,
	useCreateGageCalibrationMutation,
	useCreateGageCalibrationDocumentMutation,
	useExportGagesMutation,
} from '../../redux/api/quality';
import { useGetBuildingsQuery } from '../../redux/api/location';
import BuildingTag from '../BuildingTag';
import {
	Gage,
	GageCategory,
	GageType,
	GageCalibration,
	GageQuery,
	CalibrationStrategyValue,
	CalibrationStrategyLabel,
	GageStatusValue,
	GageStatusLabel,
	GAGE_STATUS_LABELS,
	GAGE_REMOVAL_REASON_OPTIONS,
	CalibrationStatusValue,
	CALIBRATION_LABELS,
	CALIBRATION_STRATEGY_LABELS,
} from '../../interfaces/Quality.Interface';
import {
	DownloadOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import PaginationComponent from '../Footers/Pagination';
import {
	showAddGageModal,
	closeAddGageModal,
	closeEditGageModal,
	showEditGageModal,
	showGageCalibrationsModal,
	closeGageCalibrationsModal,
} from '../../redux/slices/quality';
import dayjs from 'dayjs';
import { useQueryParameters } from '../../hooks';
import { API_DATE_FORMAT, DEFAULT_DATE_FORMAT } from '../../config/date.ts';
import { TableFilter } from '../Filters/index.tsx';

const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;

const CALIBRATION_COLUMNS: ColumnsType<GageCalibration> = [
	{
		title: 'Calibration Date',
		dataIndex: 'calibration_date',
		key: 'calibration_date',
		render: (record: GageCalibration['calibration_date']) => {
			return record ? dayjs(record).format(DEFAULT_DATE_FORMAT) : '-';
		},
	},
	{
		title: 'Calibrated By',
		dataIndex: 'calibrated_by',
		key: 'calibrated_by',
	},
	{
		title: 'Notes',
		dataIndex: 'notes',
		key: 'notes',
	},
	{
		title: 'Certificate(s)',
		key: 'documents',
		render: (_: unknown, record: GageCalibration) => {
			if (!record.documents?.length) return '-';
			const doc = record.documents[record.documents.length - 1];
			return (
				<a
					href={doc.document}
					target="_blank"
					rel="noreferrer"
					style={{ display: 'block' }}
				>
					Certificate
				</a>
			);
		},
	},
];

const getCalibrationStatusTag = (calibrationStatus: CalibrationStatusValue) => {
	const label =
		CALIBRATION_LABELS[calibrationStatus as keyof typeof CALIBRATION_LABELS] || 'N/A'
	return (
		<Tag
			color={getCalibrationStatusTagColor(
				calibrationStatus as CalibrationStatusValue,
			)}
		>
			{label}
		</Tag>
	);
};

function getTodayInCentralYYYYMMDD(): string {
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'America/Chicago',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});
	const parts = formatter.formatToParts(new Date());
	const year = parts.find((p) => p.type === 'year')?.value ?? '';
	const month = parts.find((p) => p.type === 'month')?.value ?? '';
	const day = parts.find((p) => p.type === 'day')?.value ?? '';
	return `${year}-${month}-${day}`;
}

const calculateCalibrationStatus = (
	calibrationStrategy: string,
	calibrationDate: dayjs.Dayjs | null | undefined,
) => {
	if (!calibrationStrategy || !calibrationDate) {
		return 'N/A';
	}
	const d = dayjs(calibrationDate);
	if (!d.isValid()) {
		return 'N/A';
	}
	const dateStr = d.format('YYYY-MM-DD');
	if (!dateStr || dateStr === 'Invalid Date') {
		return 'N/A';
	}
	// Due date = calibration date + 1 year (plain date math, no timezone)
	const dueDateStr = dayjs(dateStr).add(1, 'year').format('YYYY-MM-DD');
	const todayStr = getTodayInCentralYYYYMMDD();
	// Compare as strings (YYYY-MM-DD sorts lexicographically)
	if (dueDateStr < todayStr) {
		return CalibrationStatusValue.OUT_OF_CALIBRATION;
	}
	return CalibrationStatusValue.IN_CALIBRATION;
};

const calculateCalibrationDueDate = (
	calibrationStrategy: string,
	calibrationDate: dayjs.Dayjs | null | undefined,
) => {
	if (!calibrationStrategy || !calibrationDate) {
		return 'N/A';
	}
	const d = dayjs(calibrationDate);
	if (!d.isValid()) {
		return 'N/A';
	}
	return d.add(1, 'year').format(DEFAULT_DATE_FORMAT);
};

export function getCalibrationStatusTagColor(name: CalibrationStatusValue) {
	const colors = {
		[CalibrationStatusValue.IN_CALIBRATION]: 'lime',
		[CalibrationStatusValue.OUT_OF_CALIBRATION]: 'volcano',
	};

	return colors[name] || 'default';
}

export const getLatestCalibration = ({
	manufacturerCalibrationDate,
	calibrations,
}: {
	calibrations: GageCalibration[];
	manufacturerCalibrationDate: dayjs.Dayjs | null | undefined;
}): dayjs.Dayjs | null | undefined => {
	const validManufacturer = manufacturerCalibrationDate && dayjs(manufacturerCalibrationDate).isValid()
		? dayjs(manufacturerCalibrationDate)
		: null;
	if (calibrations.length > 0) {
		const fromCalibrations = calibrations
			.map((c) => dayjs(c.calibration_date))
			.filter((d) => d.isValid());
		const allCalibrations = [...fromCalibrations];
		if (validManufacturer) {
			allCalibrations.push(validManufacturer);
		}
		if (allCalibrations.length === 0) {
			return validManufacturer ?? undefined;
		}
		return allCalibrations.sort((a, b) => b.diff(a))[0];
	}
	return validManufacturer ?? manufacturerCalibrationDate ?? undefined;
};

const QualityGagesTable = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryParams = useQueryParameters();
	const currentPageUrl = Number(queryParams.get('page')) || 1;
	const searchUrl = queryParams.get('search') || '';
	const ordering = queryParams.get('ordering') || '-created_time';

	const [searchValue, setSearchValue] = useState<string>(searchUrl);
	const [filters, setFilters] = useState<Record<string, any>>({});

	const calibrationStatusParam = queryParams.get('calibration_status') || '';
	const calibrationStrategyParam = queryParams.get('calibration_strategy') || '';
	const categoryParam = queryParams.get('type__category__name') || '';
	const typeParam = queryParams.get('type__name') || '';
	const statusParam = queryParams.get('status') || '';
	const internalSerialNumberParam = queryParams.get('internal_serial_number') || '';
	const rangeParam = queryParams.get('range') || '';
	const makeParam = queryParams.get('make') || '';
	const modelParam = queryParams.get('model') || '';
	const descriptionParam = queryParams.get('description') || '';
	const locationParam = queryParams.get('location') || '';
	const buildingParam = queryParams.get('building__name') || '';
	const calibrationDueDateFromParam = queryParams.get('calibration_due_date_from') || '';
	const calibrationDueDateToParam = queryParams.get('calibration_due_date_to') || '';

	const orderQuery: GageQuery = {
		page: currentPageUrl,
		search: searchUrl,
		ordering: ordering,
		...(calibrationStatusParam && { calibration_status: calibrationStatusParam }),
		...(calibrationStrategyParam && { calibration_strategy: calibrationStrategyParam }),
		...(categoryParam && { type__category__name: categoryParam }),
		...(typeParam && { type__name: typeParam }),
		...(statusParam && { status: statusParam }),
		...(buildingParam && { building__name: buildingParam }),
		...filters,
	};

	const { data, error, isLoading, isFetching, refetch } =
		useGetGagesQuery(orderQuery);

	const [createGage] = useCreateGageMutation();
	const [updateGage] = useUpdateGageMutation();
	const [createGageCalibration] = useCreateGageCalibrationMutation();
	const [createGageCalibrationDocument] = useCreateGageCalibrationDocumentMutation();
	const [exportGages, { isLoading: isExporting }] = useExportGagesMutation();
	const {
		addGageModalVisible,
		editGageModalVisible,
		gageCalibrationsModalVisible,
	} = useSelector((state: any) => state.quality);

	const [form] = Form.useForm();
	const [editForm] = Form.useForm();
	const [calibrationForm] = Form.useForm();
	const [currentGage, setCurrentGage] = useState<Gage | null>(null);
	const [calibrationModalVisible, setCalibrationModalVisible] = useState(false);
	const [fileList, setFileList] = useState<any[]>([]);
	const [calibrationFileList, setCalibrationFileList] = useState<any[]>([]);

	const { data: categories } = useGetGageCategoriesQuery();
	const { data: buildings } = useGetBuildingsQuery();
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
		undefined,
	);
	const { data: allGageTypes, refetch: refetchGageTypes } = useGetGageTypesQuery({});
	const gageTypes = selectedCategory
		? allGageTypes?.filter(type => type.category.id === selectedCategory)
		: allGageTypes;
	const createCalibrationStrategy = Form.useWatch('calibration_strategy', form);
	const editCalibrationStrategy = Form.useWatch(
		'calibration_strategy',
		editForm,
	);
	const createCalibrationDate = Form.useWatch(
		'manufacturer_calibration_date',
		form,
	) as dayjs.Dayjs;
	const editCalibrationDate = Form.useWatch(
		'manufacturer_calibration_date',
		editForm,
	) as dayjs.Dayjs;
	const createStatus = Form.useWatch('status', form);
	const editStatus = Form.useWatch('status', editForm);

	const latestCalibration = getLatestCalibration({
		manufacturerCalibrationDate: editGageModalVisible
			? editCalibrationDate
			: createCalibrationDate,
		calibrations: (currentGage?.calibrations as GageCalibration[]) || [],
	});

	const calibrationStatus = calculateCalibrationStatus(
		editGageModalVisible ? editCalibrationStrategy : createCalibrationStrategy,
		latestCalibration,
	);

	const calibrationDueDate = calculateCalibrationDueDate(
		editGageModalVisible ? editCalibrationStrategy : createCalibrationStrategy,
		latestCalibration,
	);

	const isOutOfCalibration =
		calibrationStatus === CalibrationStatusValue.OUT_OF_CALIBRATION;

	useEffect(() => {
		// Edit modal keeps auto-flagging out-of-calibration gages as removed from service.
		if (isOutOfCalibration && editGageModalVisible) {
			editForm.setFieldsValue({ status: GageStatusValue.REMOVED_FROM_SERVICE });
		}
		// Add modal never forces a status: older / self-calibrated gages are entered
		// first and calibrated afterward, so the user must stay free to pick In Service.
		if (addGageModalVisible) {
			const currentStatus = form.getFieldValue('status');
			if (!currentStatus) {
				form.setFieldsValue({ status: GageStatusValue.IN_SERVICE });
			}
		}
	}, [isOutOfCalibration, editGageModalVisible, addGageModalVisible, editForm, form]);

	useEffect(() => {
		if (selectedCategory) {
			refetchGageTypes();
		}
	}, [selectedCategory, refetchGageTypes]);

	useEffect(() => {
		setSearchValue(searchUrl);
	}, [searchUrl]);

	const handleSearch = (value: string) => {
		queryParams.set('search', value);
		navigate({
			pathname: '/quality-gages',
			search: queryParams.toString(),
		});
	};

	const handleFilterChange = (filterKey: string, value: any) => {
		const newFilters = { ...filters };
		if (value === null || value === undefined || value === '') {
			delete newFilters[filterKey];
		} else {
			newFilters[filterKey] = value;
		}
		setFilters(newFilters);

		if (value === null || value === undefined || value === '') {
			queryParams.delete(filterKey);
		} else {
			queryParams.set(filterKey, value);
		}

		navigate({
			pathname: '/quality-gages',
			search: queryParams.toString(),
		});
	};

	const handleResetFilters = () => {
		setFilters({});
		setSearchValue('');
		const filterKeys = [
			'type__category__name',
			'type__name',
			'building__name',
			'make',
			'model',
			'mfg_serial_number',
			'internal_serial_number',
			'location',
			'description',
			'range',
			'status',
			'calibration_strategy',
			'purchase_date_from',
			'purchase_date_to',
			'manufacturer_calibration_date_from',
			'manufacturer_calibration_date_to',
			'calibration_due_date_from',
			'calibration_due_date_to',
			'calibration_status',
		];
		filterKeys.forEach((key) => queryParams.delete(key));
		queryParams.delete('search');
		navigate({
			pathname: '/quality-gages',
			search: queryParams.toString(),
		});
	};

	const handleCalibrationUploadChange = ({
		fileList: newFileList,
	}: {
		fileList: any[];
	}) => {
		setCalibrationFileList(newFileList);
		return newFileList;
	};

	const uploadProps = {
		onRemove: (file: File) => {
			setFileList((prevFileList) => {
				const index = prevFileList.indexOf(file);
				const newFileList = prevFileList.slice();
				newFileList.splice(index, 1);
				return newFileList;
			});
		},
		beforeUpload: (file: File) => {
			setFileList((prevFileList) => [...prevFileList, file]);
			return false;
		},
		fileList,
	};

	const calibrationUploadProps = {
		onRemove: (file: File) => {
			setCalibrationFileList((prevFileList) => {
				const index = prevFileList.indexOf(file);
				const newFileList = prevFileList.slice();
				newFileList.splice(index, 1);
				return newFileList;
			});
		},
		beforeUpload: (file: File) => {
			setCalibrationFileList((prevFileList) => [...prevFileList, file]);
			return false;
		},
		fileList: calibrationFileList,
	};

	if (error) {
		navigate('/orders', { replace: true });
	}

	if (!data) {
		return <LoadingOutlined />;
	}

	const { count, results } = data;

	const handleTableChange: TableProps<Gage>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra,
	) => {
		if (extra.action === 'filter') {
			if (_filters['calibration_status'] && _filters['calibration_status'].length > 0) {
				queryParams.set('calibration_status', _filters['calibration_status'].join(','));
			} else {
				queryParams.delete('calibration_status');
			}

			if (_filters['calibration_strategy'] && _filters['calibration_strategy'].length > 0) {
				queryParams.set('calibration_strategy', _filters['calibration_strategy'].join(','));
			} else {
				queryParams.delete('calibration_strategy');
			}

			if (_filters['type__category__name'] && _filters['type__category__name'].length > 0) {
				queryParams.set('type__category__name', _filters['type__category__name'].join(','));
			} else {
				queryParams.delete('type__category__name');
			}

			if (_filters['type__name'] && _filters['type__name'].length > 0) {
				queryParams.set('type__name', _filters['type__name'].join(','));
			} else {
				queryParams.delete('type__name');
			}

			if (_filters['status'] && _filters['status'].length > 0) {
				queryParams.set('status', _filters['status'].join(','));
			} else {
				queryParams.delete('status');
			}

			if (_filters['building__name'] && _filters['building__name'].length > 0) {
				queryParams.set('building__name', _filters['building__name'].join(','));
			} else {
				queryParams.delete('building__name');
			}

			navigate({
				pathname: '/quality-gages',
				search: queryParams.toString(),
			});
		}

		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult<Gage>)?.order;
			const columnKey = (sorter as SorterResult<Gage>)?.columnKey;

			if (columnKey && orderType) {
				const column = orderType === 'ascend' ? String(columnKey) : '-' + String(columnKey);
				queryParams.set('ordering', column);
			} else {
				queryParams.delete('ordering');
			}
			navigate({
				pathname: '/quality-gages',
				search: queryParams.toString(),
			});
		}
	};

	const handleOk = async () => {
		try {
			// Validate the form fields
			const values = await form.validateFields();

			// Create a FormData object to hold the form values
			const formData = new FormData();

			// Explicitly append each form value to the FormData object
			formData.append('category', values.category);
			formData.append('type', values.type);
			formData.append('make', values.make);
			formData.append('model', values.model);
			formData.append('mfg_serial_number', values.mfg_serial_number);
			formData.append('internal_serial_number', values.internal_serial_number);
			if (values.purchase_date) {
				formData.append(
					'purchase_date',
					dayjs(values.purchase_date).format(API_DATE_FORMAT),
				);
			}
			if (values.manufacturer_calibration_date) {
				formData.append(
					'manufacturer_calibration_date',
					dayjs(values.manufacturer_calibration_date).format(API_DATE_FORMAT),
				);
			}
			formData.append('calibration_strategy', values.calibration_strategy);
			formData.append('status', values.status);
			if (values.removal_reason) {
				formData.append('removal_reason', values.removal_reason);
			}
			if (values.building) {
				formData.append('building', values.building);
			}
			if (values.location) {
				formData.append('location', values.location);
			}

			if (values.range && values.range.trim() !== '') {
				formData.append('range', values.range);
			}
			if (values.description && values.description.trim() !== '') {
				formData.append('description', values.description);
			}

			if (
				values.manufacturer_calibration_certificate &&
				values.manufacturer_calibration_certificate.length > 0
			) {
				formData.append(
					'manufacturer_calibration_certificate',
					values.manufacturer_calibration_certificate[0].originFileObj,
				);
			}

			// Send the FormData object using your createGage function
			await createGage(formData).unwrap();

			// Reset the form fields and close the modal
			form.resetFields();
			dispatch(closeAddGageModal());
			refetch(); // Refetch the gage list after a new gage is successfully added
		} catch (errorInfo) {
			console.log('Failed:', errorInfo);
		}
	};

	const handleEditOk = async () => {
		try {
			if (currentGage) {
				const values = await editForm.validateFields();
				const updateData: any = {
					...values,
					purchase_date: values.purchase_date
						? dayjs(values.purchase_date).format(API_DATE_FORMAT)
						: null,
				};

				// Only include manufacturer_calibration_date if it exists
				if (values.manufacturer_calibration_date) {
					updateData.manufacturer_calibration_date = dayjs(
						values.manufacturer_calibration_date,
					).format(API_DATE_FORMAT);
				}

				await updateGage({
					id: currentGage.id,
					values: updateData,
				}).unwrap();
				editForm.resetFields();
				dispatch(closeEditGageModal());
				setCurrentGage(null);
				refetch(); // Refetch the gage list after a gage is successfully updated to get the restored status
			}
		} catch (errorInfo) {
			console.log('Failed:', errorInfo);
		}
	};

	const handleExport = async () => {
		try {
			const { url, warning } = await exportGages().unwrap();

			// The link is handed over for the user to click rather than opened
			// for them: any window.open here lands after the await, which is
			// exactly what popup blockers stop.
			notification.success({
				message: 'Gage list exported',
				duration: 0,
				description: (
					<>
						{warning && <div style={{ marginBottom: 8 }}>{warning}</div>}
						<a href={url} target="_blank" rel="noopener noreferrer">
							Open the “ERP - Gage List” sheet
						</a>
					</>
				),
			});
		} catch (errorInfo) {
			notification.error({
				message: 'Export failed',
				description:
					'The gage list could not be written to Google Sheets. Please try again.',
			});
		}
	};

	const handleCancel = () => {
		form.resetFields();
		dispatch(closeAddGageModal());
	};

	const handleEditCancel = () => {
		editForm.resetFields();
		dispatch(closeEditGageModal());
		setCurrentGage(null);
	};

	const handleGageCalibrationCancel = () => {
		calibrationForm.resetFields();
		dispatch(closeGageCalibrationsModal());
		setCurrentGage(null);
	};

	const handleNewCalibrationCancel = () => {
		calibrationForm.resetFields();
		setCalibrationModalVisible(false);
	};

	const handleNewCalibrationOk = async () => {
		try {
			const values = await calibrationForm.validateFields();
			const newCalibration = await createGageCalibration({
				...values,
				calibration_date: dayjs(values.calibration_date).format(API_DATE_FORMAT),
				gage: currentGage?.id,
			}).unwrap();

			if (values.calibration_certificate?.length > 0) {
				const formData = new FormData();
				formData.append('calibration', newCalibration.id);
				formData.append('document', values.calibration_certificate[0].originFileObj);
				await createGageCalibrationDocument(formData as any).unwrap();
			}

			calibrationForm.resetFields();
			setCalibrationFileList([]);
			setCalibrationModalVisible(false);
			dispatch(closeGageCalibrationsModal());
			refetch();
		} catch (errorInfo) {
			console.log('Failed:', errorInfo);
		}
	};

	const handleEditClick = (gage: Gage) => {
		setCurrentGage(gage);
		setSelectedCategory(gage.type.category.id); // Set the selected category to the gage's category
		refetchGageTypes(); // Refetch the gage types for the selected category
		editForm.setFieldsValue({
			...gage,
			category: gage.type.category.id,
			type: gage.type.id,
			building: gage.building?.id,
			purchase_date: gage.purchase_date ? dayjs(gage.purchase_date) : null,
			calibration_due_date: gage.calibration_due_date
				? dayjs(gage.calibration_due_date)
				: null,
			manufacturer_calibration_date: gage.manufacturer_calibration_date
				? dayjs(gage.manufacturer_calibration_date)
				: null,
		});
		dispatch(showEditGageModal());
	};

	const handleEditCalibrations = (gage: Gage) => {
		setCurrentGage(gage);
		dispatch(showGageCalibrationsModal());
	};

	const handleRecordNewCalibration = () => {
		setCalibrationModalVisible(true);
	};

	const calibrationStatusFilters = [
		{ text: CALIBRATION_LABELS[CalibrationStatusValue.IN_CALIBRATION], value: CalibrationStatusValue.IN_CALIBRATION },
		{ text: CALIBRATION_LABELS[CalibrationStatusValue.OUT_OF_CALIBRATION], value: CalibrationStatusValue.OUT_OF_CALIBRATION },
	]
	const calibrationStrategyFilters = [
		{ text: CALIBRATION_STRATEGY_LABELS[CalibrationStrategyValue.ON_DEMAND], value: CalibrationStrategyValue.ON_DEMAND },
		{ text: CALIBRATION_STRATEGY_LABELS[CalibrationStrategyValue.ANNUAL], value: CalibrationStrategyValue.ANNUAL },
	]
	const gageStatusFilters = [
		{ text: GageStatusLabel.IN_SERVICE, value: GageStatusValue.IN_SERVICE },
		{ text: GageStatusLabel.REMOVED_FROM_SERVICE, value: GageStatusValue.REMOVED_FROM_SERVICE },
		{ text: GageStatusLabel.FOR_REFERENCE_ONLY, value: GageStatusValue.FOR_REFERENCE_ONLY },
	]
	const categoryFilters = categories?.map((category: GageCategory) => ({
		text: category.name,
		value: category.name,
	})) || []

	const typeFilters = allGageTypes?.map((type: GageType) => ({
		text: type.name,
		value: type.name,
	})) || []

	const buildingFilters = buildings?.map((b) => ({
		text: b.name,
		value: b.name,
	})) || []

	// Helper function to get sort order for a column
	const getSortOrder = (columnKey: string) => {
		if (ordering === columnKey) return 'ascend';
		if (ordering === '-' + columnKey) return 'descend';
		return undefined;
	};

	const tableColumns: ColumnsType<Gage> = [
		{
			title: 'Internal Serial Number',
			dataIndex: 'internal_serial_number',
			key: 'internal_serial_number',
			sorter: true,
			sortOrder: getSortOrder('internal_serial_number'),
			filteredValue: internalSerialNumberParam ? [internalSerialNumberParam] : null,
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search internal serial number"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('internal_serial_number', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.();
						handleFilterChange('internal_serial_number', null);
						confirm();
					}}
				/>
			),
		},
		{
			title: 'Category',
			key: 'type__category__name',
			sorter: true,
			sortOrder: getSortOrder('type__category__name'),
			render: (record: Gage) => record?.type?.category?.name,
			filters: categoryFilters,
			filteredValue: categoryParam ? categoryParam.split(',') : null,
		},
		{
			title: 'Type',
			key: 'type__name',
			sorter: true,
			sortOrder: getSortOrder('type__name'),
			render: (record: Gage) => record?.type?.name,
			filters: typeFilters,
			filteredValue: typeParam ? typeParam.split(',') : null,
		},
		{
			title: 'Gage Status',
			dataIndex: 'status',
			key: 'status',
			sorter: true,
			sortOrder: getSortOrder('status'),
			filters: gageStatusFilters,
			filteredValue: queryParams.get('status') ? queryParams.get('status')!.split(',') : null,
			render: (record: Gage['status']) => {
				return GAGE_STATUS_LABELS[record as GageStatusValue] || record;
			},
		},
		{
			title: 'Range',
			dataIndex: 'range',
			key: 'range',
			sorter: true,
			sortOrder: getSortOrder('range'),
			filteredValue: rangeParam ? [rangeParam] : null,
			render: (record: Gage['range']) => {
				return record || '-';
			},
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search range"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('range', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.()
						handleFilterChange('range', null);
						confirm();
					}}
				/>
			),
		},
		{
			title: 'Make',
			dataIndex: 'make',
			key: 'make',
			sorter: true,
			sortOrder: getSortOrder('make'),
			filteredValue: makeParam ? [makeParam] : null,
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search make"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('make', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.()
						handleFilterChange('make', null);
						confirm();
					}}
				/>
			),
		},
		{
			title: 'Model',
			dataIndex: 'model',
			key: 'model',
			sorter: true,
			sortOrder: getSortOrder('model'),
			filteredValue: modelParam ? [modelParam] : null,
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search model"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('model', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.()
						handleFilterChange('model', null);
						confirm();
					}}
				/>
			),
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
			sorter: true,
			sortOrder: getSortOrder('description'),
			filteredValue: descriptionParam ? [descriptionParam] : null,
			render: (record: Gage['description']) => {
				return record || '-';
			},
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search description"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('description', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.()
						handleFilterChange('description', null);
						confirm();
					}}
				/>
			),
		},
		{
			title: 'Location',
			dataIndex: 'location',
			key: 'location',
			sorter: true,
			sortOrder: getSortOrder('location'),
			filteredValue: locationParam ? [locationParam] : null,
			render: (record: Gage['location']) => {
				return record || '-';
			},
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => (
				<TableFilter
					type="search"
					placeholder="Search location"
					value={selectedKeys[0]}
					onChange={setSelectedKeys}
					onSubmit={() => {
						handleFilterChange('location', selectedKeys[0]);
						confirm();
					}}
					onReset={() => {
						clearFilters?.()
						handleFilterChange('location', null);
						confirm();
					}}
				/>
			),
		},
	{
		title: 'Building',
		dataIndex: 'building',
		key: 'building__name',
		sorter: true,
		sortOrder: getSortOrder('building__name'),
		filters: buildingFilters,
		filteredValue: buildingParam ? buildingParam.split(',') : null,
		render: (record: Gage['building']) => (
			<BuildingTag name={record?.name} showIcon={false} />
		),
	},
	{
		title: 'Calibration Status',
		dataIndex: 'calibration_status',
			key: 'calibration_status',
			sorter: true,
			sortOrder: getSortOrder('calibration_status'),
			filters: calibrationStatusFilters,
			filteredValue: calibrationStatusParam ? calibrationStatusParam.split(',') : null,
			render: (record: Gage['calibration_status']) => {
				return getCalibrationStatusTag(record as CalibrationStatusValue);
			},
		},
		{
			title: 'Calibration Due Date',
			dataIndex: 'calibration_due_date',
			key: 'calibration_due_date',
			sorter: true,
			sortOrder: getSortOrder('calibration_due_date'),
			render: (record: Gage['calibration_due_date']) => {
				return record ? dayjs(record).format(DEFAULT_DATE_FORMAT) : '-';
			},
			filteredValue: calibrationDueDateFromParam || calibrationDueDateToParam ? [calibrationDueDateFromParam, calibrationDueDateToParam] : null,
			filterDropdown: ({
				setSelectedKeys,
				selectedKeys,
				confirm,
				clearFilters,
			}: FilterDropdownProps) => {
				const value = selectedKeys && selectedKeys.length === 2 && selectedKeys[0] && selectedKeys[1]
					? [dayjs(selectedKeys[0] as string), dayjs(selectedKeys[1] as string)]
					: undefined;

				return (
					<TableFilter
						type="date-range"
						placeholder="Search calibration due date"
						value={value as any}
						onChange={setSelectedKeys}
						onSubmit={() => {
							if (selectedKeys && selectedKeys.length === 2) {
								const newFilters = { ...filters };
								newFilters['calibration_due_date_from'] = selectedKeys[0];
								newFilters['calibration_due_date_to'] = selectedKeys[1];

								setFilters(newFilters);

								// Update query params
								queryParams.set('calibration_due_date_from', String(selectedKeys[0]));
								queryParams.set('calibration_due_date_to', String(selectedKeys[1]));

								navigate({
									pathname: '/quality-gages',
									search: queryParams.toString(),
								});
							} else {
								console.log('selectedKeys not valid:', selectedKeys);
							}
							confirm();
						}}
						onReset={() => {
							setSelectedKeys([]);
							clearFilters?.();
							handleFilterChange('calibration_due_date_from', null);
							handleFilterChange('calibration_due_date_to', null);
							confirm();
						}}
					/>
				);
			},
		},
		{
			title: 'Calibration Strategy',
			dataIndex: 'calibration_strategy',
			key: 'calibration_strategy',
			sorter: true,
			sortOrder: getSortOrder('calibration_strategy'),
			filters: calibrationStrategyFilters,
			filteredValue: calibrationStrategyParam ? calibrationStrategyParam.split(',') : null,
			render: (record: Gage['calibration_strategy']) => {
				return CALIBRATION_STRATEGY_LABELS[record as CalibrationStrategyValue];
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (record: Gage) => (
				<>
					<Button type="link" onClick={() => handleEditClick(record)}>
						View/Edit
					</Button>
					<Button type="link" onClick={() => handleEditCalibrations(record)}>
						Calibrations
					</Button>
				</>
			),
		},
	];

	const tableFooter = () => {
		return (
			<div className="flex justify-between items-center">
				<div className="ml-auto">
					<PaginationComponent pageSize={20} total={count} />
				</div>
			</div>
		);
	};

	const emptyTable = () => {
		return (
			<Empty
				description={
					<span style={{ color: '#000' }}>
						There are no gages to be displayed.
					</span>
				}
			/>
		);
	};

	return (
		<div>
			<div
				style={{
					marginBottom: 16,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<Search
						placeholder="Search gages"
						onSearch={handleSearch}
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						style={{ width: 300 }}
					/>
					<Button onClick={handleResetFilters}>Reset</Button>
				</div>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<Tooltip title="Exports every gage to the “ERP - Gage List” Google Sheet, ignoring the filters above.">
						<Button
							icon={<DownloadOutlined />}
							loading={isExporting}
							onClick={handleExport}
						>
							Export
						</Button>
					</Tooltip>
					<Button type="primary" onClick={() => dispatch(showAddGageModal())}>
						Add New Gage
					</Button>
				</div>
			</div>

			<Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<CustomResponsiveTable
						columns={tableColumns}
						data={results}
						pagination={false}
						loading={!data || isLoading || isFetching}
						footer={tableFooter}
						onChange={handleTableChange}
						emptyTable={emptyTable}
					/>
				</div>
			</Card>
			<Modal
				title="Add New Gage"
				open={addGageModalVisible}
				onOk={handleOk}
				onCancel={handleCancel}
				centered
			>
				<Form form={form} layout="vertical" requiredMark={false}>
					<Row gutter={[8, 16]} className="mt-5">
						<Col span={12}>
							<Form.Item
								name="category"
								label="Category"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Select
									placeholder="Select a category"
									onChange={(value) => setSelectedCategory(value)}
								>
									{categories?.map((category: GageCategory) => (
										<Option key={category.id} value={category.id}>
											{category.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="type" label="Type" rules={[{ required: true }]}>
								<Select placeholder="Select a type">
									{gageTypes?.map((type: GageType) => (
										<Option key={type.id} value={type.id}>
											{type.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item name="make" label="Make" rules={[{ required: true }]}>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="model"
								label="Model"
								rules={[{ required: true }]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="mfg_serial_number"
								label="Mfg. Serial Number"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="internal_serial_number"
								label="Internal Serial Number"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="purchase_date"
								label="Date of purchase"
							>
								<DatePicker format={DEFAULT_DATE_FORMAT} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="manufacturer_calibration_date"
								label="Manufacturer Calibration Date"
							>
								<DatePicker format={DEFAULT_DATE_FORMAT} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="calibration_due_date"
								label="Calibration Due Date"
							>
								<Text>{calibrationDueDate}</Text>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="calibration_strategy"
								label="Calibration Strategy"
								rules={[{ required: true }]}
							>
								<Select placeholder="Select calibration strategy">
									<Option value={CalibrationStrategyValue.ON_DEMAND}>
										{CalibrationStrategyLabel.ON_DEMAND}
									</Option>
									<Option value={CalibrationStrategyValue.ANNUAL}>
										{CalibrationStrategyLabel.ANNUAL}
									</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="calibration_status" label="Calibration Status">
								{getCalibrationStatusTag(
									calibrationStatus as CalibrationStatusValue,
								)}
							</Form.Item>
						</Col>
					</Row>
				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item
							name="building"
							label="Building"
							rules={[{ required: true }]}
						>
							<Select placeholder="Select a building">
								{buildings?.map((building) => (
									<Option key={building.id} value={building.id}>
										{building.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="location"
							label="Location"
						>
							<Input placeholder="Enter location" />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item
							name="status"
							label="Gage Status"
							rules={[{ required: true }]}
							initialValue={GageStatusValue.IN_SERVICE}
						>
							<Select placeholder="Select gage status">
								<Option value={GageStatusValue.IN_SERVICE}>
									{GageStatusLabel.IN_SERVICE}
								</Option>
								<Option value={GageStatusValue.REMOVED_FROM_SERVICE}>
									{GageStatusLabel.REMOVED_FROM_SERVICE}
								</Option>
								<Option value={GageStatusValue.FOR_REFERENCE_ONLY}>
									{GageStatusLabel.FOR_REFERENCE_ONLY}
								</Option>
							</Select>
						</Form.Item>
					</Col>
					{createStatus === GageStatusValue.REMOVED_FROM_SERVICE && (
						<Col span={12}>
							<Form.Item
								name="removal_reason"
								label="Removal Reason"
								rules={[{ required: true, message: 'Select why the gage is being removed' }]}
							>
								<Select placeholder="Select removal reason" options={GAGE_REMOVAL_REASON_OPTIONS} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item name="range" label="Range">
							<Input placeholder='e.g., 0-8", thread type, etc.' />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={[8, 16]}>
					<Col span={24}>
						<Form.Item name="description" label="Description">
							<Input.TextArea rows={3} placeholder="Enter description" />
						</Form.Item>
					</Col>
				</Row>

				{/* Manufacturer Calibration Certificate Upload Field */}
				<Form.Item
					name="manufacturer_calibration_certificate"
						label="Manufacturer Calibration Certificate"
						valuePropName="fileList"
						getValueFromEvent={(e: any) => {
							console.log('Upload event:', e);
							if (Array.isArray(e)) {
								return e;
							}
							return e?.fileList;
						}}
					>
						<Upload {...uploadProps} listType="picture">
							<Button icon={<UploadOutlined />}>Upload Certificate</Button>
						</Upload>
					</Form.Item>
				</Form>
			</Modal>
			<Modal
				title="Edit Gage"
				open={editGageModalVisible}
				onOk={handleEditOk}
				onCancel={handleEditCancel}
				centered
			>
				<Form form={editForm} layout="vertical" requiredMark={false}>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="category"
								label="Category"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Select
									placeholder="Select a category"
									value={selectedCategory}
									onChange={(value) => setSelectedCategory(value)}
								>
									{categories?.map((category: GageCategory) => (
										<Option key={category.id} value={category.id}>
											{category.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="type" label="Type" rules={[{ required: true }]}>
								<Select
									placeholder="Select a type"
									value={editForm.getFieldValue('type')}
								>
									{gageTypes?.map((type: GageType) => (
										<Option key={type.id} value={type.id}>
											{type.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item name="make" label="Make" rules={[{ required: true }]}>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="model"
								label="Model"
								rules={[{ required: true }]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="mfg_serial_number"
								label="Mfg. Serial Number"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="internal_serial_number"
								label="Internal Serial Number"
								rules={[
									{
										required: true,
									},
								]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="purchase_date"
								label="Date of purchase"
								rules={[
									{
										required: true,
									},
								]}
							>
								<DatePicker format={DEFAULT_DATE_FORMAT} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="manufacturer_calibration_date"
								label="Manufacturer Calibration Date"
							>
								<DatePicker format={DEFAULT_DATE_FORMAT} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="calibration_due_date"
								label="Calibration Due Date"
							>
								<Text>{calibrationDueDate}</Text>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="calibration_strategy"
								label="Calibration Strategy"
								rules={[{ required: true }]}
							>
								<Select placeholder="Select calibration strategy">
									<Option value={CalibrationStrategyValue.ON_DEMAND}>
										{CalibrationStrategyLabel.ON_DEMAND}
									</Option>
									<Option value={CalibrationStrategyValue.ANNUAL}>
										{CalibrationStrategyLabel.ANNUAL}
									</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="calibration_status" label="Calibration Status">
								{getCalibrationStatusTag(
									calibrationStatus as CalibrationStatusValue,
								)}
							</Form.Item>
						</Col>
					</Row>
				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item name="building" label="Building">
							<Select placeholder="Select a building" allowClear>
								{buildings?.map((building) => (
									<Option key={building.id} value={building.id}>
										{building.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="location"
							label="Location"
						>
							<Input placeholder="Enter location" />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item
							name="status"
							label="Gage Status"
							rules={[{ required: true }]}
						>
							<Select
								placeholder="Select gage status"
								disabled={isOutOfCalibration}
							>
								{isOutOfCalibration ? (
									<Option value={GageStatusValue.REMOVED_FROM_SERVICE}>
										{GageStatusLabel.REMOVED_FROM_SERVICE}
									</Option>
								) : (
									<>
										<Option value={GageStatusValue.IN_SERVICE}>
											{GageStatusLabel.IN_SERVICE}
										</Option>
										<Option value={GageStatusValue.REMOVED_FROM_SERVICE}>
											{GageStatusLabel.REMOVED_FROM_SERVICE}
										</Option>
										<Option value={GageStatusValue.FOR_REFERENCE_ONLY}>
											{GageStatusLabel.FOR_REFERENCE_ONLY}
										</Option>
									</>
								)}
							</Select>
						</Form.Item>
					</Col>
					{editStatus === GageStatusValue.REMOVED_FROM_SERVICE && (
						<Col span={12}>
							<Form.Item
								name="removal_reason"
								label="Removal Reason"
								rules={[{ required: true, message: 'Select why the gage is being removed' }]}
							>
								<Select placeholder="Select removal reason" options={GAGE_REMOVAL_REASON_OPTIONS} />
							</Form.Item>
						</Col>
					)}
				</Row>

				<Row gutter={[8, 16]}>
					<Col span={12}>
						<Form.Item name="range" label="Range">
							<Input placeholder='e.g., 0-8", thread type, etc.' />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={[8, 16]}>
					<Col span={24}>
						<Form.Item name="description" label="Description">
							<Input.TextArea rows={3} placeholder="Enter description" />
						</Form.Item>
					</Col>
				</Row>

				{currentGage?.manufacturer_calibration_certificate ? (
						<a
							href={currentGage.manufacturer_calibration_certificate}
							target="_blank"
							rel="noreferrer"
						>
							Manufacturer Calibration Cert
						</a>
					) : null}

					{/* Manufacturer Calibration Certificate Upload Field */}
					{/* <Form.Item
						name="manufacturer_calibration_certificate"
						label="Manufacturer Calibration Certificate"
						valuePropName="fileList"
						getValueFromEvent={(e: any) => {
							console.log('Upload event:', e);
							if (Array.isArray(e)) {
								return e;
							}
							return e?.fileList;
						}}
					>
						<Upload {...uploadProps} listType="picture">
							<Button icon={<UploadOutlined />}>Upload Certificate</Button>
						</Upload>
					</Form.Item> */}
				</Form>
			</Modal>
			<Modal
				title="Gage Calibrations"
				open={gageCalibrationsModalVisible}
				onCancel={handleGageCalibrationCancel}
				footer={null}
				centered
				width={'960px'}
			>
				<Flex
					justify="space-between"
					align="end"
					flex="column"
					className="mb-3"
				>
					<div>
						<Text>{`${currentGage?.make} ${currentGage?.model} ${currentGage?.type.name}`}</Text>
						<br />
						<Text>{`Manufacturer SN: ${currentGage?.mfg_serial_number}`}</Text>
						<br />
						<Text>{`Internal SN: ${currentGage?.internal_serial_number}`}</Text>
					</div>
				</Flex>
				<Table
					columns={CALIBRATION_COLUMNS}
					dataSource={currentGage?.calibrations || []}
					pagination={false}
					rowKey="id"
					className="m-0 p-0"
				/>
				<Button
					type="primary"
					className="w-full"
					onClick={handleRecordNewCalibration}
				>
					Record New Calibration
				</Button>
			</Modal>
			<Modal
				title="Record New Calibration"
				open={calibrationModalVisible}
				onOk={handleNewCalibrationOk}
				onCancel={handleNewCalibrationCancel}
				centered
			>
				<Form form={calibrationForm} layout="vertical" requiredMark={false}>
					<Row gutter={[8, 16]}>
						<Col span={12}>
							<Form.Item
								name="calibration_date"
								label="Calibration Date"
								rules={[{ required: true }]}
							>
								<DatePicker />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="calibrated_by"
								label="Calibrated By"
								rules={[{ required: true }]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[8, 16]}>
						<Col span={24}>
							<Form.Item name="notes" label="Notes">
								<Input.TextArea rows={4} />
							</Form.Item>
						</Col>
					</Row>
					{/* Calibration Certificate Upload Field */}
					<Form.Item
						name="calibration_certificate"
						label="Calibration Certificate"
						valuePropName="fileList"
						getValueFromEvent={handleCalibrationUploadChange}
					>
						<Upload {...calibrationUploadProps} listType="picture">
							<Button icon={<UploadOutlined />}>
								Upload Calibration Certificate
							</Button>
						</Upload>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default QualityGagesTable;
